import AddIcon from '@mui/icons-material/Add';

import { CellRuleStudyId } from 'interfaces/cellRule';
import { useCreateCellRulePanelMutation } from 'utils/queryHooks/cellRule/useCellRuleMutations';
import useCellRulePanels from 'utils/queryHooks/cellRule/useCellRulePanels';

import { Button, Divider, Grid, List, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';
import Loader, { LoadingStage } from 'components/Loader';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { includes, isEmpty, map, orderBy, partition, some } from 'lodash';
import React, { useMemo } from 'react';
import { CellRulesPanelAccordion } from './CellRulesPanel';
import { GLOBAL_PANEL_STUDY_ID } from './GlobalCellRules';
import StudyMultiplexCellRules from './StudySavedCellRules';

interface CellRulesPanelsContainerProps {
  studyId: CellRuleStudyId;
  editable?: boolean;
}

const CellRulePanelsContainer: React.FC<CellRulesPanelsContainerProps> = ({ studyId, editable = false }) => {
  const { data: panels, isLoading: isLoadingPanels } = useCellRulePanels(studyId);

  const isSpecificStudy =
    Boolean(studyId) && studyId !== GLOBAL_PANEL_STUDY_ID && !includes(NON_SPECIFIC_STUDY_VALUES, studyId);
  const panelScope = useMemo(() => ({ studyId, filters: { studyId }, slidesMode: true as const }), [studyId]);
  const multiplexPanelsQuery = useQuery({
    queryKey: getMultiplexPanelsQueryKey(panelScope),
    queryFn: ({ signal }) => getMultiplexPanels(panelScope, signal),
    enabled: isSpecificStudy,
  });
  const hasMultiplexPanels = (multiplexPanelsQuery.data?.panels.length ?? 0) > 0;
  const createMutation = useCreateCellRulePanelMutation();

  const handleSave = () => {
    createMutation.mutate({ studyId, panel: { label: 'New Panel' } });
  };

  return (
    <>
      <StudyMultiplexCellRules studyId={studyId} editable={editable} />
      <List
        sx={{
          '& .MuiListItem-root': {
            px: 0,
          },
        }}
      >
        {isLoadingPanels ? (
          <Loader stage={LoadingStage.LoadingOperationsSettings} />
        ) : isEmpty(panels) ? (
          hasMultiplexPanels ? null : (
            <Typography>No panels found, create a new one?</Typography>
          )
        ) : (
          (() => {
            const sorted = orderBy(panels, [(p) => p.updatedAt || p.createdAt], ['desc']);
            const [approved, unapproved] = partition(sorted, (p) => !isEmpty(p.approvedAt));
            return (
              <>
                {map(approved, (panel) => (
                  <CellRulesPanelAccordion
                    key={panel.id}
                    panel={panel}
                    studyId={studyId}
                    editable={editable}
                    otherApprovedPanelsExist={some(panels, (p) => p.id !== panel.id && !isEmpty(p.approvedAt))}
                  />
                ))}
                {!isEmpty(approved) && !isEmpty(unapproved) && <Divider sx={{ my: 2 }} />}
                {map(unapproved, (panel) => (
                  <CellRulesPanelAccordion
                    key={panel.id}
                    panel={panel}
                    studyId={studyId}
                    editable={editable}
                    otherApprovedPanelsExist={some(panels, (p) => p.id !== panel.id && !isEmpty(p.approvedAt))}
                  />
                ))}
              </>
            );
          })()
        )}
      </List>
      {editable && !hasMultiplexPanels && (
        <Button onClick={handleSave} startIcon={<AddIcon />}>
          {createMutation.isLoading ? (
            <Grid container alignItems="center" spacing={1}>
              <Grid item>Create new panel... </Grid>
              <Grid item>
                <Loader size="small" />
              </Grid>
            </Grid>
          ) : (
            'Add new Panel'
          )}
        </Button>
      )}
    </>
  );
};

export default CellRulePanelsContainer;
